import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Copy } from "lucide-react";
import { fetchOnboardingStatus } from "../../api/CompanyApi";
import "./EssentialSetup.css";

/*
 * Checklist sederhana di dalam banner sambutan dashboard: 4 langkah penting agar
 * organisasi langsung bisa memakai Sewoork. Status dihitung dari data (bukan dicentang manual):
 *   1. Template jadwal ada        2. Template payroll ada
 *   3. Ada karyawan selain owner  4. Ada karyawan yang sudah login di aplikasi mobile
 * Disembunyikan setelah keempatnya selesai.
 */

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.sewoork.app";
const APP_STORE_URL = "https://apps.apple.com/app/sewoork/id6779068695";

// Logo toko aplikasi (SVG sama dengan halaman OnBoarding).
const GooglePlayLogo = () => (
  <svg viewBox="30 336.7 120.9 129.2" width="22" height="22" aria-hidden="true">
    <path fill="#FFD400" d="M119.2,421.2c15.3-8.4,27-14.8,28-15.3c3.2-1.7,6.5-6.2,0-9.7c-2.1-1.1-13.4-7.3-28-15.3l-20.1,20.2L119.2,421.2z" />
    <path fill="#FF3333" d="M99.1,401.1l-64.2,64.7c1.5,0.2,3.2-0.2,5.2-1.3c4.2-2.3,48.8-26.7,79.1-43.3L99.1,401.1L99.1,401.1z" />
    <path fill="#48FF48" d="M99.1,401.1l20.1-20.2c0,0-74.6-40.7-79.1-43.1c-1.7-1-3.6-1.3-5.3-1L99.1,401.1z" />
    <path fill="#3BCCFF" d="M99.1,401.1l-64.3-64.3c-2.6,0.6-4.8,2.9-4.8,7.6c0,7.5,0,107.5,0,113.8c0,4.3,1.7,7.4,4.9,7.7L99.1,401.1z" />
  </svg>
);

const AppStoreLogo = () => (
  <svg viewBox="0 0 384 512" width="20" height="22" aria-hidden="true">
    <path
      fill="currentColor"
      d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
    />
  </svg>
);

const CopyButton = ({ value, label, copiedLabel }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      // Clipboard diblokir: pengguna tetap bisa menyalin manual dari kolom teks.
    }
  };

  return (
    <button type="button" className="es-copy" onClick={copy}>
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? copiedLabel : label}
    </button>
  );
};

const EssentialSetup = ({ companyId, companyName }) => {
  const { t } = useTranslation("dashboard");
  const [status, setStatus] = useState(null);
  const [showShare, setShowShare] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    const response = await fetchOnboardingStatus(companyId);
    if (response?.status === 200) {
      setStatus(response.data);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!status) return null;

  const steps = [
    {
      id: "schedule",
      done: status.schedule_templates > 0,
      content: <Link to="/schedules/form">{t("essentials.schedule")}</Link>,
    },
    {
      id: "payroll",
      done: status.payroll_templates > 0,
      content: <Link to="/payroll/form">{t("essentials.payroll")}</Link>,
    },
    {
      id: "staff",
      done: status.staff_total > 0,
      content: (
        <>
          <Link to="/users/new">{t("essentials.staff")}</Link>
          <span className="es-list__or">{t("essentials.or")}</span>
          <Link to="/users/import">{t("essentials.staff_import")}</Link>
        </>
      ),
    },
    {
      id: "app",
      done: status.staff_on_app > 0,
      content: (
        <button type="button" onClick={() => setShowShare(true)}>
          {t("essentials.app")}
        </button>
      ),
    },
  ];

  const doneCount = steps.filter((step) => step.done).length;
  if (doneCount === steps.length) return null;

  const shareMessage = t("essentials.share.message", {
    company: companyName || "",
    android: PLAY_STORE_URL,
    ios: APP_STORE_URL,
  });

  return (
    <>
      <div className="es-banner">
        <div className="es-banner__head">
          <span className="es-banner__title">{t("essentials.title")}</span>
          <span className="es-banner__count">
            {t("essentials.progress", { done: doneCount, total: steps.length })}
          </span>
        </div>
        <div className="es-banner__bar" aria-hidden="true">
          <div style={{ "--es-progress": `${(doneCount / steps.length) * 100}%` }} />
        </div>
        <ul className="es-list">
          {steps.map((step, index) => (
            <li key={step.id} className={step.done ? "is-done" : ""} style={{ "--i": index }}>
              <span className="es-list__check" aria-hidden="true">
                {step.done && <Check size={12} strokeWidth={3.5} />}
              </span>
              <span className="visually-hidden">
                {step.done ? t("essentials.done") : t("essentials.todo")}:
              </span>
              <span className="es-list__label">{step.content}</span>
            </li>
          ))}
        </ul>
      </div>

      <Modal show={showShare} onHide={() => setShowShare(false)} centered className="es-share-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t("essentials.share.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="es-share__intro">{t("essentials.share.intro")}</p>

          <div className="es-share__link">
            <span className="es-share__logo">
              <GooglePlayLogo />
            </span>
            <div>
              <strong>Google Play</strong>
              <span>{PLAY_STORE_URL}</span>
            </div>
            <CopyButton value={PLAY_STORE_URL} label={t("essentials.share.copy")} copiedLabel={t("essentials.share.copied")} />
          </div>
          <div className="es-share__link">
            <span className="es-share__logo es-share__logo--apple">
              <AppStoreLogo />
            </span>
            <div>
              <strong>App Store</strong>
              <span>{APP_STORE_URL}</span>
            </div>
            <CopyButton value={APP_STORE_URL} label={t("essentials.share.copy")} copiedLabel={t("essentials.share.copied")} />
          </div>

          <label className="es-share__label" htmlFor="es-share-message">
            {t("essentials.share.message_label")}
          </label>
          <textarea id="es-share-message" className="form-control es-share__message" rows={6} readOnly value={shareMessage} />

          <div className="es-share__actions">
            <CopyButton value={shareMessage} label={t("essentials.share.copy_message")} copiedLabel={t("essentials.share.copied")} />
            <a
              className="es-share__whatsapp"
              href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("essentials.share.whatsapp")}
            </a>
          </div>
          <p className="es-share__note">{t("essentials.share.note")}</p>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default EssentialSetup;
